<script setup lang="ts">
import { computed } from 'vue'

import BaseIcon from '@/components/base/BaseIcon.vue'
import BaseRailItem from '@/components/base/BaseRailItem.vue'
import type { NavSection } from '@/constants/app/navigation.constant.js'

const props = defineProps<{ sections: readonly NavSection[]; activeId: string; menuOpen: boolean }>()

const emit = defineEmits<{ select: [string]; more: [] }>()

/**
 * The rail, relaid along the bottom edge for a phone.
 *
 * Same dark surface, same items, same tokens — it IS the rail, so an operator moving
 * between desk and phone finds the sections where they left them. Four sections fit as
 * 44 px targets with a readable label on a 360 px screen; the rest sit behind "More",
 * which opens the sheet. The visible four are the FIRST four, in rail order, never the
 * most recent: a tab bar whose tabs move is a tab bar nobody can learn.
 */
const VISIBLE_TABS = 4

const tabs = computed(() => props.sections.slice(0, VISIBLE_TABS))
const overflow = computed(() => props.sections.slice(VISIBLE_TABS))
// "More" lights when the active section is behind it, so the bar always shows where you are.
const moreActive = computed(() => props.menuOpen || overflow.value.some((section) => section.id === props.activeId))
</script>

<template>
  <nav
    class="flex shrink-0 items-stretch border-t border-rail-active bg-rail pb-safe lg:hidden"
    aria-label="Sections"
  >
    <div
      v-for="section in tabs"
      :key="section.id"
      class="min-w-0 flex-1"
    >
      <BaseRailItem
        :active="section.id === activeId"
        @click="emit('select', section.id)"
      >
        <BaseIcon
          :name="section.icon"
          :size="22"
        />
        <!--
          `text-caption`, not the rail's `text-micro`. Five tabs on a 390px phone give each
          ~78px, and "PROMOTIONS" uppercase with the micro step's 0.07em tracking needs
          ~85px — it truncated to "PROMOTI…". Sentence case at 12px is what a bottom tab
          bar is everywhere, it fits, and it is still a step on the scale: the uppercase
          micro step is what a 92px vertical rail column can afford and a tab cannot.
        -->
        <span class="max-w-full truncate-cell text-caption leading-tight">{{ section.label }}</span>
      </BaseRailItem>
    </div>

    <div
      v-if="overflow.length > 0"
      class="min-w-0 flex-1"
    >
      <BaseRailItem
        :active="moreActive"
        aria-haspopup="dialog"
        :aria-expanded="menuOpen"
        @click="emit('more')"
      >
        <BaseIcon
          name="dots-three"
          :size="22"
        />
        <span class="text-caption leading-tight">More</span>
      </BaseRailItem>
    </div>
  </nav>
</template>
